import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, onMounted, onBeforeUnmount, onBeforeMount } from 'vue'
import { Icons } from '@/icons/icons'
import SpinnerLayerComponent from '@/components/Spinner/SpinnerLayerComponent.vue'
import NoResultsComponent from '@/components/search/NoResults/NoResultsComponent.vue'
import useTemplates from '@/components/template/composables/useTemplates'
import type { ITemplate } from '@/store/modules/template/templateTypes'
import { useI18n } from 'vue-i18n-composable'
import { configuracionPanelURLS } from '@/router/routes/urlRoutes'
import type { TemplateTreeItem } from '@/components/template/TemplateSelectorComponent/types/TemplateTypes'


export default /*#__PURE__*/_defineComponent({
  __name: 'TemplateSelectorTreeComponent',
  props: {
  prop: {
    type: String,
    default: ''
  },
  idEntityType: {
    type: [Number, String],
    required: true
  },
  loading: {
    type: Boolean,
    default: false
  },
  generateTemplate: {
    type: Boolean,
    default: false
  }
},
  emits: ['templateSelected'],
  setup(__props, { emit }) {

const props = __props;

// IMPORTS ==========================================================
const { t } = useI18n()

// DEFINE PROPS  ==========================================================


// EVENTS  ==========================================================


// COMPOSABLES ==========================================================
const { templateTree, templateTreeEntityType, fetchTemplateTree, setTreeEntityType } = useTemplates()

// DATA ==========================================================
const componentKey = ref(0)
const isLoading = ref(props.loading)
const templateSelected = ref<ITemplate | null>(null)
const itemsOpenend = ref<number[]>([])
const generatingTemplate = ref(props.generateTemplate)

// CONSTANTS ==========================================================
const texts = {
  noTemplatesMessage: t('components.template_selector_tree.no_templates_message'),
  createTemplate: t('components.template_selector_tree.create_template'),
  noThanks: t('components.template_selector_tree.no_thanks'),
  templatesNotFound: t('components.file_manager.dialogs.templates_not_found'),
  generatingTemplate: t('components.file_manager.dialogs.generating_document'),
  emptyFolder: t('components.file_manager.advises.empty_folder'),
  templatesFromSettings: t('components.file_manager.dialogs.templates_from_settings', {
    link: configuracionPanelURLS.DOCUMENTS_TEMPLATES
  })
}

const icons = {
  document: Icons.ARCHIVE_EMPTY,
  folder: Icons.FOLDER,
  folderOpen: Icons.FOLDER_OPEN,
  on: Icons.RADIO_ACTIVE,
  off: Icons.CIRCLE_O,
  close: Icons.CLOSE
}

// COMPUTED ==========================================================
const spinnerText = computed(() => {
  return generatingTemplate.value ? texts.generatingTemplate : ''
})

const hasTemplates = computed(() => {
  return Array.isArray(templateTree.value) && templateTree.value.length > 0
})

// METHODS ==========================================================
const updateComponent = () => {
  componentKey.value += 1
  return componentKey.value
}

const getTemplateTreeData = async () => {
  const json = {
    idEntityType: props.idEntityType
  }
  await fetchTemplateTree(json)
}

const directoryIsOpen = (itemId: number) => {
  return itemsOpenend.value.includes(itemId)
}

const directoryIcon = (itemId: number) => {
  return directoryIsOpen(itemId) ? icons.folderOpen : icons.folder
}

const directoryIsEmpty = (item: TemplateTreeItem) => {
  return directoryIsOpen(item.id) && typeof item.children !== 'undefined' && item.children.length <= 0
}

const addInitiallyOpenItems = (itemId: number) => {
  itemsOpenend.value.push(itemId)
}

const removeInitiallyOpenItems = (itemId: number) => {
  itemsOpenend.value = itemsOpenend.value.filter((_itemId) => _itemId !== itemId)
}

const updateOpenItems = (itemId: number) => {
  if (!directoryIsOpen(itemId)) {
    addInitiallyOpenItems(itemId)
    return true
  } else {
    removeInitiallyOpenItems(itemId)
    return false
  }
}

const spinnerTreeItem = (item: TemplateTreeItem, loading: boolean) => {
  item.loading = loading
  updateComponent()
}

const addChildrenTreeItems = async (item: TemplateTreeItem) => {
  const childrenUndefined = typeof item.children === 'undefined'
  let data = !childrenUndefined ? item.children : []

  if (childrenUndefined) {
    data = await fetchTemplateTree({
      idEntityType: props.idEntityType,
      id: item.id,
      getData: true
    })
  }

  item.children = data || []
  item.locked = (data?.length || 0) <= 0
}

const onClickDirectory = async (item: any) => {
  const updateOpenItemsResult = updateOpenItems(item.id)
  if (!updateOpenItemsResult) {
    return false
  }

  spinnerTreeItem(item, true)

  await addChildrenTreeItems(item).then(() => {
    spinnerTreeItem(item, false)
  })
}

const getTemplate = () => {
  emit('templateSelected', templateSelected.value)
}

// HOOKS ==========================================================
onBeforeMount(() => {
  isLoading.value = true
})

onMounted(async () => {
  if (props.idEntityType !== templateTreeEntityType.value || !hasTemplates.value) {
    setTreeEntityType(Number(props.idEntityType))
    await getTemplateTreeData()
  }
  isLoading.value = props.loading
  generatingTemplate.value = props.generateTemplate
})

onBeforeUnmount(() => {
  templateSelected.value = null
  isLoading.value = true
  generatingTemplate.value = true
})

return { __sfc: true,t, props, emit, templateTree, templateTreeEntityType, fetchTemplateTree, setTreeEntityType, componentKey, isLoading, templateSelected, itemsOpenend, generatingTemplate, texts, icons, spinnerText, hasTemplates, updateComponent, getTemplateTreeData, directoryIsOpen, directoryIcon, directoryIsEmpty, addInitiallyOpenItems, removeInitiallyOpenItems, updateOpenItems, spinnerTreeItem, addChildrenTreeItems, onClickDirectory, getTemplate, SpinnerLayerComponent, NoResultsComponent }
}

})